import { Suspense, useMemo } from 'react'
import { Outlet } from 'react-router'
import { CompanyBillingProvider } from '@/components/company-billing/provider'
import { CompanySuspensionProvider } from '@/components/company-suspension/provider'
import { SamlSessionBoundary } from '@/components/error-boundaries/saml-session-boundary'
import { Layout } from '@/components/layout'
import { LayoutSkeleton } from '@/components/layout/layout-skeleton'
import { MixpanelIdentifier } from '@/components/mixpanel/mixpanel-identifier'
import { OnboardingSheetProvider } from '@/components/onboarding-sheet/context'
import {
  PaymentMethodGateContent,
  PaymentMethodGateProvider,
} from '@/components/payment-method-gate'
import { RBACProvider } from '@/components/rbac/rbac-provider'
import {
  USER_COMPANIES_QUERY,
  USER_PERMISSIONS_QUERY,
  TOTAL_SERVICES_QUERY,
  COMPANY_SUSPENSION_QUERY,
  COMPANY_BILLING_QUERY,
  SAML_INTERSTITIAL_QUERY,
} from '@/components/root/gql'
import { QueryRefsContext } from '@/components/root/query-refs-context'
import { SamlSessionProvider } from '@/components/saml-session/saml-session-provider'
import { useIdCompany, useBackgroundQuery } from '@/lib/hooks'
import type {
  CompanySuspensionQuery,
  CompanySuspensionQueryVariables,
  TotalServicesQuery,
  TotalServicesQueryVariables,
  UserPermissionsQuery,
  UserPermissionsQueryVariables,
  UserCompaniesQuery,
  UserCompaniesQueryVariables,
  CompanyBillingQuery,
  CompanyBillingQueryVariables,
  SamlInterstitialQuery,
} from '@/components/root/gql.types.gen'
import type {} from '@/components/root/gql.types.gen'

export const RootContent = () => {
  const { idCompany } = useIdCompany()
  const [userCompaniesQueryRef] = useBackgroundQuery<
    UserCompaniesQuery,
    UserCompaniesQueryVariables
  >(USER_COMPANIES_QUERY)
  const [userPermissionsQueryRef] = useBackgroundQuery<
    UserPermissionsQuery,
    UserPermissionsQueryVariables
  >(USER_PERMISSIONS_QUERY, {
    variables: {
      idCompany: idCompany ?? '',
    },
  })
  const [totalServicesQueryRef] = useBackgroundQuery<
    TotalServicesQuery,
    TotalServicesQueryVariables
  >(TOTAL_SERVICES_QUERY, {
    variables: {
      idCompany: idCompany ?? '',
    },
  })
  const [companySuspensionQueryRef] = useBackgroundQuery<
    CompanySuspensionQuery,
    CompanySuspensionQueryVariables
  >(COMPANY_SUSPENSION_QUERY, {
    variables: {
      idCompany: idCompany ?? '',
    },
  })
  const [companyBillingQueryRef] = useBackgroundQuery<
    CompanyBillingQuery,
    CompanyBillingQueryVariables
  >(COMPANY_BILLING_QUERY, {
    variables: {
      idCompany: idCompany ?? '',
    },
  })
  const [samlInterstitialQueryRef] = useBackgroundQuery<SamlInterstitialQuery>(
    SAML_INTERSTITIAL_QUERY
  )

  const queryRefsContext = useMemo(
    () => ({
      userCompaniesQueryRef,
      userPermissionsQueryRef,
      totalServicesQueryRef,
      companySuspensionQueryRef,
      companyBillingQueryRef,
      samlInterstitialQueryRef,
    }),
    [
      userCompaniesQueryRef,
      userPermissionsQueryRef,
      totalServicesQueryRef,
      companySuspensionQueryRef,
      companyBillingQueryRef,
      samlInterstitialQueryRef,
    ]
  )

  return (
    <Suspense key={idCompany} fallback={<LayoutSkeleton />}>
      <QueryRefsContext.Provider value={queryRefsContext}>
        <RBACProvider>
          <MixpanelIdentifier />
          <SamlSessionProvider>
            <CompanySuspensionProvider>
              <CompanyBillingProvider>
                <PaymentMethodGateProvider>
                  <OnboardingSheetProvider>
                    <Layout>
                      <SamlSessionBoundary>
                        <PaymentMethodGateContent>
                          <Outlet />
                        </PaymentMethodGateContent>
                      </SamlSessionBoundary>
                    </Layout>
                  </OnboardingSheetProvider>
                </PaymentMethodGateProvider>
              </CompanyBillingProvider>
            </CompanySuspensionProvider>
          </SamlSessionProvider>
        </RBACProvider>
      </QueryRefsContext.Provider>
    </Suspense>
  )
}
